// ─── Dragging a booking ──────────────────────────────────────────────────────
//
// The arithmetic and the rules, separate from the pointer plumbing, because
// the interesting failures here are not about events.
//
// ── The rule that shapes all of this ────────────────────────────────────────
//
// A drop is a RESCHEDULE. It goes to the same code path a typed reschedule
// does, and that path can refuse: the slot may have been taken between the
// grab and the release, and `@nulife/booking` will raise rather than
// double-book.
//
// So the calendar must never settle a block into its new position until the
// change is confirmed. An optimistic move that silently reverts is the worst
// of both — the hairdresser sees the appointment land, looks away, and it is
// somewhere else. The component shows an explicit in-flight state instead, and
// on failure puts the block back AND says why.

/** Snap granularity, matching the booking module's default slot grid. */
export const SNAP_MINUTES = 15;

/** Nothing may be dragged shorter than this. A zero-length booking is not a
 *  booking, and the database's positive-duration check would refuse it
 *  anyway — better to make it impossible to express than to be told. */
export const MIN_DURATION_MINUTES = 15;

export type DragMode = "move" | "resize";

export type DragProposal = {
  startsAt: Date;
  endsAt: Date;
  /** Minutes the block actually shifted after snapping and clamping — 0 means
   *  the drag ended where it began and nothing should be sent. */
  deltaMinutes: number;
};

/** Round a pixel delta to whole snap steps. */
export function snapMinutes(deltaPixels: number, pixelsPerMinute: number, snap = SNAP_MINUTES): number {
  if (pixelsPerMinute <= 0) return 0;
  return Math.round(deltaPixels / pixelsPerMinute / snap) * snap;
}

export type DragBounds = {
  /** Visible window, in minutes from midnight. */
  windowStartMinute: number;
  windowEndMinute: number;
};

const minuteOf = (d: Date) => d.getHours() * 60 + d.getMinutes();
const withMinute = (day: Date, minute: number) =>
  new Date(day.getFullYear(), day.getMonth(), day.getDate(), Math.floor(minute / 60), minute % 60);

/**
 * Where a drag would put a booking.
 *
 * Returns the proposal only; it commits nothing. Clamped to the visible window
 * so a block cannot be dragged somewhere it could not then be seen — the same
 * reasoning as clamping on render.
 */
export function proposeDrag(
  original: { startsAt: Date; endsAt: Date },
  mode: DragMode,
  deltaPixels: number,
  pixelsPerMinute: number,
  bounds: DragBounds,
  snap = SNAP_MINUTES,
): DragProposal {
  const delta = snapMinutes(deltaPixels, pixelsPerMinute, snap);
  const startMinute = minuteOf(original.startsAt);
  const endMinute = minuteOf(original.endsAt);
  const duration = endMinute - startMinute;

  if (mode === "move") {
    // Clamp the whole block, keeping its duration — a move must never also
    // change how long something takes.
    const lowest = bounds.windowStartMinute;
    const highest = bounds.windowEndMinute - duration;
    const proposed = Math.min(Math.max(startMinute + delta, lowest), highest);
    return {
      startsAt: withMinute(original.startsAt, proposed),
      endsAt: withMinute(original.startsAt, proposed + duration),
      deltaMinutes: proposed - startMinute,
    };
  }

  // Resize moves the END only. The start is where the customer is expected,
  // and dragging the bottom edge must not quietly move their arrival time.
  const lowestEnd = startMinute + MIN_DURATION_MINUTES;
  const proposedEnd = Math.min(Math.max(endMinute + delta, lowestEnd), bounds.windowEndMinute);
  return {
    startsAt: original.startsAt,
    endsAt: withMinute(original.startsAt, proposedEnd),
    deltaMinutes: proposedEnd - endMinute,
  };
}

/**
 * Would this proposal collide with something that reserves time?
 *
 * A courtesy check so the interface can refuse a drop before sending it, and
 * show the block returning rather than a failure message. It is NOT the
 * guarantee — the database's exclusion constraint is, and this cannot see a
 * booking somebody else made a moment ago.
 *
 * Deliberately excludes the booking being dragged, and anything that does not
 * reserve time: a requested booking may sit under a confirmed one.
 */
export function collidesWith(
  proposal: { startsAt: Date; endsAt: Date },
  others: { id: string; startsAt: Date; endsAt: Date; status: string }[],
  draggedId: string,
): boolean {
  return others.some(o =>
    o.id !== draggedId &&
    (o.status === "confirmed" || o.status === "blocked") &&
    o.startsAt.getTime() < proposal.endsAt.getTime() &&
    proposal.startsAt.getTime() < o.endsAt.getTime());
}
